import { useCallback } from 'react';
import { supabaseData } from '../../utils/supabase-data-client';
import { Bookmark } from '../../types/bookmarks';
import { PostgrestError } from '@supabase/supabase-js';
import { useCacheStore } from './useCacheStore';
import { useCacheFirstQuery } from './useCacheFirstQuery';

interface UseBookmarksByCategoryReturn {
  data: Bookmark[] | undefined;
  loading: boolean;
  error: PostgrestError | null;
  refetch: () => Promise<void>;
}

const useBookmarksByCategory = (
  categoryId: string | null | undefined
): UseBookmarksByCategoryReturn => {
  const cache = useCacheStore();

  const cacheReader = cache.getBookmarks;

  const cacheFilter = useCallback(
    (items: Bookmark[]) =>
      items
        .filter((b) => b.category_id === categoryId)
        .sort((a, b) => a.bookmark_name.localeCompare(b.bookmark_name)),
    [categoryId]
  );

  const networkFetch = useCallback(async () => {
    const { data, error } = await supabaseData
      .from('bookmarks')
      .select<'*', Bookmark>('*')
      .order('bookmark_name');

    if (data) {
      cache.setBookmarks(data as Bookmark[]);
      // Return filtered subset for this specific query
      const filtered = (data as Bookmark[]).filter(
        (b) => b.category_id === categoryId
      );
      return { data: filtered, error };
    }

    return { data: data as Bookmark[] | null, error };
  }, [cache, categoryId]);

  const { data, loading, error, refetch } = useCacheFirstQuery<Bookmark>({
    cacheReader,
    cacheFilter,
    networkFetch,
    enabled: !!categoryId,
    queryKey: categoryId ?? null,
  });

  return { data, loading, error, refetch };
};

export default useBookmarksByCategory;
